import {
  SheetRevisionEntityType,
  useRestoreSheetRevision,
  useSheetRevisionDetail,
  useSheetRevisions,
} from "@/api/sheet-revision/sheet-revision";
import StyledDrawer from "@/components/StyledDrawer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { cn, isRichTextHtml, sanitizeRichTextHtml } from "@/lib/utils";
import { TFunction } from "i18next";
import { ChevronRight, History, Loader2 } from "lucide-react";
import { ReactNode } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../ui/badge";

type PayloadV1 = {
  schemaVersion: 1;
  changedKeys: string[];
  changes: Record<string, { from: unknown; to: unknown }>;
  restoredFromRevisionId?: string;
};

function parsePayloadV1(payload: unknown): PayloadV1 | null {
  if (!payload || typeof payload !== "object") return null;
  const o = payload as Record<string, unknown>;
  if (o.schemaVersion !== 1) return null;
  if (!o.changes || typeof o.changes !== "object") return null;
  return o as unknown as PayloadV1;
}

function formatActorName(
  actor: { name?: string; lastName?: string; login?: string } | null,
): string {
  if (!actor) return "—";
  const n = [actor.name, actor.lastName].filter(Boolean).join(" ").trim();
  return n || actor.login || "—";
}

const LOCALE_KEY_RE = /^[a-z]{2}(?:-[a-z]{2})?$/i;

/** Legacy FRA/GB twins superseded by Json multilingual — hide in history UI. */
const LEGACY_MULTILINGUAL_TWIN_KEYS = new Set([
  "oeuvresDescriptionTechniqueFRA",
  "oeuvresDescriptionTechniqueGB",
  "oeuvresDescriptionSupportFRA",
  "oeuvresDescriptionSupportGB",
  "oeuvresDescriptionSignatureLocalisationFRA",
  "oeuvresDescriptionSignatureLocalisationGB",
  "estampesMultiplesDescriptionTechniqueFRA",
  "estampesMultiplesDescriptionTechniqueGB",
  "estampesMultiplesDescriptionSupportMateriauFRA",
  "estampesMultiplesDescriptionSupportMateriauGB",
]);

function isEffectivelyEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return true;
    return entries.every(([, nested]) => isEffectivelyEmptyValue(nested));
  }
  return false;
}

function renderStringValue(value: string): ReactNode {
  if (!value.trim()) return "—";
  if (!isRichTextHtml(value)) return value;

  return (
    <div
      className="prose prose-xs max-w-none text-current [&_.spacing-paragraph-4]:my-1"
      dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(value) }}
    />
  );
}

function stringifyVal(v: unknown, t: TFunction): ReactNode {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return renderStringValue(v);

  if (typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>);
    if (entries.length === 0 || isEffectivelyEmptyValue(v)) return null;

    const localeEntries = entries.filter(([k]) => LOCALE_KEY_RE.test(k));
    const isLocaleObject = localeEntries.length === entries.length;

    if (isLocaleObject) {
      return (
        <div className="space-y-1">
          {localeEntries
            .filter(([, value]) => !isEffectivelyEmptyValue(value))
            .map(([locale, value]) => {
              const localeLabel = t(
                `i18n.languages.${locale.toLowerCase()}.minified`,
                locale.toUpperCase(),
              );
              const renderedValue =
                typeof value === "boolean"
                  ? value
                    ? t("yes")
                    : t("no")
                  : typeof value === "string"
                    ? renderStringValue(value)
                    : String(value);

              return (
                <div key={locale} className="leading-5">
                  <span className="font-medium">{localeLabel}</span>{" "}
                  <span className="whitespace-pre-wrap break-words">
                    {renderedValue}
                  </span>
                </div>
              );
            })}
        </div>
      );
    }

    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  return String(v);
}

function formatActionLabel(
  action: "create" | "update" | "restore",
  createdAt: string,
  t: TFunction,
): string {
  const date = new Date(createdAt).toLocaleDateString("fr-FR");
  switch (action) {
    case "create":
      return `${t("element.created.date", { date })} ${date}`;
    case "restore":
      return `${t("element.restored.date", { date })} ${date}`;
    case "update":
    default:
      return `${t("element.updated.date", { date })} ${date}`;
  }
}

type SheetHistoryDrawerProps = {
  entityType: SheetRevisionEntityType;
  entityId: string;
  onRestoreSuccess?: () => void;
  menuItemTrigger?: boolean;
};

export function SheetHistoryDrawer({
  entityType,
  entityId,
  onRestoreSuccess,
  menuItemTrigger = false,
}: SheetHistoryDrawerProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader";
  const [open, setOpen] = useState(false);
  const [expandedRevisionId, setExpandedRevisionId] = useState<
    string | undefined
  >(undefined);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setExpandedRevisionId(undefined);
  };

  const listQuery = useSheetRevisions(entityType, entityId, {
    enabled: open,
  });

  const detailQuery = useSheetRevisionDetail(expandedRevisionId ?? null, {
    enabled: open && Boolean(expandedRevisionId),
  });

  const detailPayload = useMemo(
    () => parsePayloadV1(detailQuery.data?.payload),
    [detailQuery.data?.payload],
  );

  const detailMatchesExpanded =
    Boolean(expandedRevisionId) && detailQuery.data?.id === expandedRevisionId;

  const restoreMutation = useRestoreSheetRevision(entityType, entityId, () => {
    onRestoreSuccess?.();
    setExpandedRevisionId(undefined);
    toast({
      title: t("history.restored.success"),
      variant: "success",
    });
  });

  const title = t("history.title");

  const trigger = menuItemTrigger ? (
    <Button
      type="button"
      variant="ghost"
      className="h-auto w-full justify-start gap-2 rounded-sm px-2 py-1.5 text-sm font-normal shadow-none hover:bg-slate-200 focus:bg-slate-200 focus-visible:bg-slate-200"
    >
      <History className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
      {title}
    </Button>
  ) : (
    <Button type="button" variant="outline" className="gap-1">
      <History className="w-5 h-5 text-[#64748B]" />
    </Button>
  );

  return (
    <StyledDrawer
      title={<span>{title}</span>}
      trigger={trigger}
      sheetProps={{ open, onOpenChange: handleOpenChange }}
      maxWidth="sm:max-w-[500px]"
      hideSeparator
    >
      <div className="flex flex-col px-0 -mx-4">
        {listQuery.isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}
        {listQuery.data?.items.length === 0 && !listQuery.isLoading && (
          <p className="text-sm text-muted-foreground py-6 text-center px-4">
            {t("element.noHistory")}
          </p>
        )}
        <Accordion
          type="single"
          collapsible
          value={expandedRevisionId}
          onValueChange={(v) => setExpandedRevisionId(v || undefined)}
          className="border-t border-slate-200"
        >
          {listQuery.data?.items.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              className="border-slate-200 px-0"
            >
              <AccordionTrigger
                className={cn(
                  "group py-3 px-5 hover:no-underline",
                  "[&>svg:last-child]:hidden",
                )}
              >
                <div className="flex min-w-0 flex-1 items-start gap-2 text-left">
                  <ChevronRight
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200",
                      "group-data-[state=open]:rotate-90",
                    )}
                  />
                  <div className="min-w-0">
                    <div className="text-sm leading-5 text-slate-700">
                      {formatActionLabel(item.action, item.createdAt, t)}
                    </div>
                    <div className="mt-1 leading-7 font-normal text-slate-800">
                      {formatActorName(item.actor)}
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-4 pt-0">
                {expandedRevisionId === item.id && detailQuery.isLoading && (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                {expandedRevisionId === item.id &&
                  !detailQuery.isLoading &&
                  detailQuery.isError && (
                    <p className="text-sm text-destructive py-2">
                      {t("error.detailError")}
                    </p>
                  )}
                {expandedRevisionId === item.id &&
                  detailMatchesExpanded &&
                  !detailQuery.isLoading &&
                  !detailQuery.isError &&
                  !detailPayload && (
                    <p className="text-sm text-muted-foreground py-2">
                      {t("element.noPayload")}
                    </p>
                  )}
                {expandedRevisionId === item.id &&
                  detailMatchesExpanded &&
                  detailPayload && (
                    <div className="flex flex-col gap-4 border-t border-slate-100 pt-3">
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>
                          {new Date(detailQuery.data!.createdAt).toLocaleString(
                            undefined,
                            {
                              dateStyle: "medium",
                              timeStyle: "short",
                            },
                          )}
                        </p>
                        <p>
                          {t("element.by")}{" "}
                          {formatActorName(detailQuery.data!.actor)}
                        </p>
                        <Badge variant="contained">
                          {t(`element.${detailQuery.data!.action}`)}
                        </Badge>
                      </div>
                      <div className="overflow-hidden rounded-md border">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="p-2 text-left font-semibold">
                                {t("element.field")}
                              </th>
                              <th className="p-2 text-left font-semibold">
                                {t("element.before")}
                              </th>
                              <th className="p-2 text-left font-semibold">
                                {t("element.after")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailPayload.changedKeys
                              .filter((key) => !LEGACY_MULTILINGUAL_TWIN_KEYS.has(key))
                              .map((key) => {
                              const ch = detailPayload.changes[key];
                              if (!ch) return null;
                              return (
                                <tr key={key} className="border-t">
                                  <td className="break-all p-2 align-top font-mono">
                                    {t(`element.${key}`)}
                                  </td>
                                  <td className="break-all p-2 align-top text-muted-foreground">
                                    {stringifyVal(ch.from, t)}
                                  </td>
                                  <td className="break-all p-2 align-top">
                                    {stringifyVal(ch.to, t)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {!isReader && (
                        <Button
                          type="button"
                          variant="default"
                          disabled={restoreMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            const ok = window.confirm(
                              t("element.restoreConfirm"),
                            );
                            if (!ok) return;
                            restoreMutation.mutate(detailQuery.data!.id, {
                              onError: () => {
                                toast({
                                  title: t("error.restoreError"),
                                  variant: "destructive",
                                });
                              },
                            });
                          }}
                        >
                          {restoreMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            t("element.restoreButton")
                          )}
                        </Button>
                      )}
                    </div>
                  )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </StyledDrawer>
  );
}
